import {
  ANALYTICS_DELTA_BEHAVIORS,
  ANALYTICS_METRIC_SEMANTICS,
  ANALYTICS_SUMMARY_BEHAVIORS,
  ANALYTICS_TIME_BEHAVIORS,
  ANALYTICS_VISUAL_TYPES,
  createAnalyticsLibraryCategory,
  createAnalyticsLibraryEntry,
  createAnalyticsSectionLibrary
} from '../../visuals/visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from '../../visuals/graphCatalog';
import { ANALYTICS_FILTER_KEYS } from '../../analyticsFilters';

const semantics = (metric, time, summary, delta = ANALYTICS_DELTA_BEHAVIORS.NONE) => ({
  metric,
  time,
  summary,
  delta
});

const SCREEN_FILTERS = [
  ANALYTICS_FILTER_KEYS.PERIOD,
  ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  ANALYTICS_FILTER_KEYS.ROLES,
  ANALYTICS_FILTER_KEYS.RECRUITERS,
  ANALYTICS_FILTER_KEYS.SOURCES
];

const COMPARISON_FILTERS = [
  ANALYTICS_FILTER_KEYS.PERIOD,
  ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  ANALYTICS_FILTER_KEYS.ROLES,
  ANALYTICS_FILTER_KEYS.RECRUITERS,
  ANALYTICS_FILTER_KEYS.SOURCES
];

const teamAnalyticsLibrary = createAnalyticsSectionLibrary({
  section: 'recruiter_performance',
  categories: [
    createAnalyticsLibraryCategory({
      id: 'recruiter_scorecards',
      label: 'Recruiter Scorecards',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'recruiter_scorecard',
          label: 'Recruiter scorecard',
          category: 'recruiter_scorecards',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.RECRUITER_CARDS,
            ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.RECRUITER_CARDS,
          defaultSize: 'large',
          defaultVariant: 'data-forward',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['recruiter', 'period', 'metric_group'],
            measures: [
              'time_to_fill',
              'hires_delivered',
              'offer_acceptance_rate',
              'pipeline_conversion_rate',
              'candidate_dropoff_rate',
              'req_load',
              'onboarding_completion_rate',
              'composite_score'
            ]
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'recruiter_radar',
          label: 'Recruiter radar',
          category: 'recruiter_scorecards',
          visualType: ANALYTICS_VISUAL_TYPES.RADAR,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.RADAR],
          defaultGraph: ANALYTICS_GRAPH_TYPES.RADAR,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['recruiter'],
            measures: [
              'composite_score',
              'time_to_fill_index',
              'hires_delivered_index',
              'offer_acceptance_rate',
              'pipeline_conversion_rate',
              'hire_quality_score'
            ]
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'load_vs_outcome',
          label: 'Load vs outcome',
          category: 'recruiter_scorecards',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.SCATTER, ANALYTICS_GRAPH_TYPES.BUBBLE_CHART, ANALYTICS_GRAPH_TYPES.TABLE],
          defaultGraph: ANALYTICS_GRAPH_TYPES.SCATTER,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: COMPARISON_FILTERS,
          graphConfig: {
            [ANALYTICS_GRAPH_TYPES.SCATTER]: {
              presentation: 'quadrant_scatter',
              quadrantLabels: {
                topLeft: 'Strong outcome · balanced load',
                topRight: 'Strong outcome · overloaded',
                bottomLeft: 'Light load · weak conversion',
                bottomRight: 'High load · weak conversion'
              }
            },
            [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: {
              presentation: 'quadrant_scatter',
              quadrantLabels: {
                topLeft: 'Strong outcome · balanced load',
                topRight: 'Strong outcome · overloaded',
                bottomLeft: 'Light load · weak conversion',
                bottomRight: 'High load · weak conversion'
              }
            }
          },
          dataShape: {
            dimensions: ['recruiter'],
            measures: ['active_reqs', 'pipeline_conversion_rate', 'hire_volume']
          }
        })
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'recruiter_speed',
      label: 'Time & Velocity',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'time_to_fill',
          label: 'Time-to-fill',
          category: 'recruiter_speed',
          visualType: ANALYTICS_VISUAL_TYPES.TIME_SERIES,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.BOX_PLOT,
            ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.BOX_PLOT,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.DURATION,
            ANALYTICS_TIME_BEHAVIORS.PERIOD_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.AVG_DURATION,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['recruiter', 'period'],
            measures: ['time_to_fill', 'distribution']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'recruiter_velocity',
          label: 'Recruiter velocity',
          category: 'recruiter_speed',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.DURATION,
            ANALYTICS_TIME_BEHAVIORS.PERIOD_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.AVG_DURATION,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['recruiter', 'period', 'velocity_metric'],
            measures: ['average_days']
          }
        })
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'recruiter_pipeline',
      label: 'Pipeline & Offer Quality',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'pipeline_conversion',
          label: 'Pipeline conversion',
          category: 'recruiter_pipeline',
          visualType: ANALYTICS_VISUAL_TYPES.MATRIX,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.HEATMAP
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.HEATMAP,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RATE,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['recruiter', 'stage'],
            measures: ['conversion_rate']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'offer_performance',
          label: 'Offer performance',
          category: 'recruiter_pipeline',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.SCATTER
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.SCATTER,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RATE,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: COMPARISON_FILTERS,
          graphConfig: {
            [ANALYTICS_GRAPH_TYPES.SCATTER]: {
              presentation: 'quadrant_scatter',
              quadrantLabels: {
                topLeft: 'High trust · low drop-off',
                topRight: 'High trust · slipping follow-through',
                bottomLeft: 'Low trust · recoverable',
                bottomRight: 'Low trust · urgent attention'
              }
            }
          },
          dataShape: {
            dimensions: ['recruiter', 'offer_metric'],
            measures: ['acceptance_rate', 'dropoff_rate']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'retention_by_recruiter',
          label: 'Hire Quality by Recruiter Beta',
          category: 'recruiter_pipeline',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.SCATTER
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.SCATTER,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RATE,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE,
            ANALYTICS_DELTA_BEHAVIORS.QOQ
          ),
          supportedFilters: COMPARISON_FILTERS,
          graphConfig: {
            [ANALYTICS_GRAPH_TYPES.SCATTER]: {
              presentation: 'quadrant_scatter',
              quadrantLabels: {
                topLeft: 'Fast enough · strong quality',
                topRight: 'Slow but strong quality',
                bottomLeft: 'Fast but low quality',
                bottomRight: 'Slow and low quality'
              }
            }
          },
          dataShape: {
            dimensions: ['recruiter', 'quality_window'],
            measures: ['quality_score', 'time_to_fill', 'hire_volume']
          },
          requiredProducts: ['employee_data']
        })
      ]
    })
  ]
});

export default teamAnalyticsLibrary;
